import React from "react";
import { Play, Pause, Volume2, X, SkipBack, SkipForward } from "lucide-react";
import { useAudio } from "../contexts/AudioContext";
import { Slider } from "./ui/slider";

export default function PersistentAudioPlayer() {
  const { currentName, playing, toggle, stop, volume, setVolume, queueInfo, next, prev } = useAudio();
  if (!currentName) return null;

  return (
    <div
      data-testid="persistent-audio-player"
      className="fixed bottom-0 left-0 right-0 z-50 bg-[#4A0E17]/95 backdrop-blur-xl border-t-2 border-[#D4AF37]/50 text-[#FAF6F0] shadow-2xl"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 flex items-center gap-4">
        {queueInfo && (
          <button data-testid="player-prev" onClick={prev} disabled={queueInfo.idx === 0} aria-label="Previous" className="text-[#F3E5AB] disabled:opacity-30"><SkipBack className="w-5 h-5" /></button>
        )}
        <button
          data-testid="persistent-audio-play-button"
          onClick={toggle}
          className="w-11 h-11 rounded-full bg-[#D4AF37] text-[#4A0E17] flex items-center justify-center hover:scale-105 transition-transform shadow-lg"
          aria-label={playing ? "Pause" : "Play"}
        >
          {playing ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
        </button>
        {queueInfo && (
          <button data-testid="player-next" onClick={next} disabled={queueInfo.idx >= queueInfo.len - 1} aria-label="Next" className="text-[#F3E5AB] disabled:opacity-30"><SkipForward className="w-5 h-5" /></button>
        )}

        <div className="flex-1 min-w-0">
          <div className="font-devanagari text-lg truncate text-[#F3E5AB]">{currentName.sanskrit}</div>
          <div className="flex items-center gap-3">
            {queueInfo && <div data-testid="player-queue-pos" className="text-xs text-[#F3E5AB]/80 font-mono">{queueInfo.idx + 1}/{queueInfo.len}</div>}
            {currentName.number != null && <div className="text-xs text-[#F3E5AB]/80 font-mono">#{String(currentName.number).padStart(4, "0")}</div>}
            <div className="text-sm text-[#FAF6F0]/90 truncate">{currentName.english} — <span className="opacity-70">{currentName.meaning?.slice(0, 60)}{currentName.meaning?.length > 60 ? "…" : ""}</span></div>
            {playing && (
              <div className="hidden sm:flex items-end gap-0.5 h-4">
                <span className="eq-bar w-1 h-full bg-[#D4AF37]"></span>
                <span className="eq-bar w-1 h-full bg-[#D4AF37]"></span>
                <span className="eq-bar w-1 h-full bg-[#D4AF37]"></span>
              </div>
            )}
          </div>
        </div>

        <div className="hidden md:flex items-center gap-2 w-36">
          <Volume2 className="w-4 h-4 text-[#F3E5AB]" />
          <Slider
            data-testid="volume-slider"
            min={0} max={100} step={1}
            value={[Math.round(volume * 100)]}
            onValueChange={(v) => setVolume(v[0] / 100)}
          />
        </div>

        <button
          data-testid="persistent-audio-close"
          onClick={stop}
          className="p-2 rounded-full hover:bg-white/10 transition-sacred"
          aria-label="Close"
        >
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}
